import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { createAlert, resendConfirmation, requestManageLink, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CheckCircle2, MailCheck, Info } from "lucide-react";

const TYPE = "price_digest";

export const FooterNewsletter = () => {
  const { t } = useApp();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error(t("pricedigest.consentRequired")); return; }
    setLoading(true);
    try {
      const r = await createAlert({ email, type: TYPE });
      setResult({ status: r.status || "created", email });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("auth.error"));
    } finally {
      setLoading(false);
    }
  };

  const sendManage = async () => {
    setBusy(true);
    try { await requestManageLink(result.email); toast.success(t("tools.alerts.manageLinkSent")); }
    catch { toast.error(t("auth.error")); } finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true);
    try { await resendConfirmation(result.email, TYPE); toast.success(t("tools.alerts.resent")); }
    catch { toast.error(t("auth.error")); } finally { setBusy(false); }
  };

  if (result) {
    if (result.status === "exists_confirmed") {
      return (
        <div className="mt-3 rounded-lg bg-white/10 p-3 text-sm" data-testid="newsletter-exists-confirmed">
          <p className="flex items-start gap-2 text-white/80"><Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> {t("tools.alerts.alreadySubscribed")}</p>
          <button type="button" onClick={sendManage} disabled={busy} data-testid="newsletter-manage-btn" className="mt-2 inline-flex items-center text-xs font-semibold text-accent underline hover:text-accent/80 disabled:opacity-60">
            {busy && <Loader2 className="me-1.5 h-3 w-3 animate-spin" />}{t("tools.alerts.managePrefs")}
          </button>
        </div>
      );
    }
    if (result.status === "exists_pending") {
      return (
        <div className="mt-3 rounded-lg bg-white/10 p-3 text-sm" data-testid="newsletter-exists-pending">
          <p className="flex items-start gap-2 text-white/80"><MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> {t("tools.alerts.alreadyPending")}</p>
          <button type="button" onClick={resend} disabled={busy} data-testid="newsletter-resend-btn" className="mt-2 inline-flex items-center text-xs font-semibold text-accent underline hover:text-accent/80 disabled:opacity-60">
            {busy && <Loader2 className="me-1.5 h-3 w-3 animate-spin" />}{t("tools.alerts.resendConfirm")}
          </button>
        </div>
      );
    }
    return (
      <div className="mt-3 flex items-start gap-2 rounded-lg bg-white/10 p-3 text-sm text-white/80" data-testid="newsletter-success">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> {t("tools.alerts.checkEmail")}
      </div>
    );
  }

  return (
    <form className="mt-3" onSubmit={onSubmit} data-testid="newsletter-form">
      <div className="flex gap-2">
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="newsletter-input"
          placeholder={t("footer.newsletter.placeholder")}
          className="h-10 border-white/20 bg-white/10 text-white placeholder:text-white/40"
        />
        <Button type="submit" disabled={loading} data-testid="newsletter-submit" className="h-10 shrink-0 bg-accent text-accent-foreground hover:bg-accent/90">
          {loading && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{t("footer.newsletter.button")}
        </Button>
      </div>
      <div className="mt-2 flex items-start gap-2 text-xs text-white/50">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="newsletter-consent"
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
          style={{ accentColor: "hsl(var(--accent))" }}
        />
        <span>
          <span className="cursor-pointer" onClick={() => setConsent((c) => !c)} data-testid="newsletter-consent-text">{t("pricedigest.consent")}</span>{" "}
          <Link to="/legal/confidentialite" onClick={(e) => e.stopPropagation()} className="font-medium text-accent underline hover:text-accent/80" data-testid="newsletter-privacy-link">
            {t("pricedigest.privacy")}
          </Link>
        </span>
      </div>
    </form>
  );
};
